// src/TaskForm.js
//
// Same pattern as ActivityForm.js - fetches a dropdown list (Activities
// this time) before rendering. `assigned_to` is plain free text, not a
// relationship, so it's just a normal text input - no dropdown needed.

import { useState, useEffect } from 'react';
import { API_BASE_URL } from './apiConfig';
import { formatApiError } from './formErrors';

function TaskForm({ token, task, onSaved, onCancel }) {
  const [activityOptions, setActivityOptions] = useState([]);

  const [activity, setActivity] = useState(task?.activity || '');
  const [title, setTitle] = useState(task?.title || '');
  const [assignedTo, setAssignedTo] = useState(task?.assigned_to || '');
  const [dueDate, setDueDate] = useState(task?.due_date || '');
  const [isCompleted, setIsCompleted] = useState(task?.is_completed || false);

  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetch(`${API_BASE_URL}/api/activities/`, {
      headers: { Authorization: `Token ${token}` },
    })
      .then((res) => res.json())
      .then((data) => {
        if (Array.isArray(data)) setActivityOptions(data);
      });
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');

    const payload = {
      activity,
      title,
      assigned_to: assignedTo,
      due_date: dueDate || null,
      is_completed: isCompleted,
    };

    const url = task
      ? `${API_BASE_URL}/api/tasks/${task.id}/`
      : `${API_BASE_URL}/api/tasks/`;
    const method = task ? 'PUT' : 'POST';

    try {
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', Authorization: `Token ${token}` },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        setError(formatApiError(await response.json()));
        setSaving(false);
        return;
      }
      onSaved();
    } catch (err) {
      setError('Could not connect to the server');
      setSaving(false);
    }
  };

  return (
    <div className="bg-white p-6 rounded shadow mb-6 max-w-lg">
      <h2 className="text-lg font-bold mb-4">{task ? 'Edit Task' : 'New Task'}</h2>
      <form onSubmit={handleSubmit}>
        <label className="block text-sm font-medium text-gray-700 mb-1">Activity</label>
        <select value={activity} onChange={(e) => setActivity(e.target.value)} required
          className="w-full border border-gray-300 rounded px-3 py-2 mb-4 focus:outline-none focus:ring-2 focus:ring-blue-400">
          <option value="" disabled>Select an activity...</option>
          {activityOptions.map((a) => (
            <option key={a.id} value={a.id}>{a.name}</option>
          ))}
        </select>

        <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
        <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} required
          className="w-full border border-gray-300 rounded px-3 py-2 mb-4 focus:outline-none focus:ring-2 focus:ring-blue-400" />

        <label className="block text-sm font-medium text-gray-700 mb-1">Assigned To</label>
        <input type="text" value={assignedTo} onChange={(e) => setAssignedTo(e.target.value)}
          placeholder="Free text for now - will become a Staff dropdown later"
          className="w-full border border-gray-300 rounded px-3 py-2 mb-4 focus:outline-none focus:ring-2 focus:ring-blue-400" />

        <label className="block text-sm font-medium text-gray-700 mb-1">Due Date</label>
        <input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)}
          className="w-full border border-gray-300 rounded px-3 py-2 mb-4 focus:outline-none focus:ring-2 focus:ring-blue-400" />

        {/* Checkbox for a boolean field - a different input type than
            anything used in ProjectForm/ActivityForm so far */}
        <label className="flex items-center gap-2 mb-4">
          <input
            type="checkbox"
            checked={isCompleted}
            onChange={(e) => setIsCompleted(e.target.checked)}
            // Checkboxes use `checked`, not `value`, and read from
            // e.target.checked (true/false), not e.target.value (text)
          />
          <span className="text-sm text-gray-700">Completed</span>
        </label>

        {error && <p className="text-red-500 text-sm mb-4 break-words">{error}</p>}

        <div className="flex gap-2">
          <button type="submit" disabled={saving}
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 disabled:opacity-50">
            {saving ? 'Saving...' : 'Save'}
          </button>
          <button type="button" onClick={onCancel}
            className="bg-gray-200 text-gray-700 px-4 py-2 rounded hover:bg-gray-300">
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}

export default TaskForm;
